
// Helper function to safely parse dates
function safeDate(d) {
  if (!d) return null;
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

// Fetches and processes email classification data
export async function getEmailClassifications() {
  try {
    const response = await fetch(process.env.REACT_APP_CLASSIFICATIONS_API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      cache: 'no-cache'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const jsonData = await response.json();

    let emailData = [];
    if (Array.isArray(jsonData)) {
      emailData = jsonData;
    } else if (jsonData.data && Array.isArray(jsonData.data)) {
      emailData = jsonData.data;
    } else if (jsonData.emails && Array.isArray(jsonData.emails)) {
      emailData = jsonData.emails;
    } else {
      throw new Error('Invalid response format. Expected array of emails.');
    }

    // Normalize data to ensure consistent field names across the app.
    // The new API returns `order_type` as an array and `priority` as a nullable value.
    // To keep backward compatibility with components that expect
    // `prediction_order_type_value`, `prediction_priority_value` and
    // `classification_timestamp` we map them here.
    // Helper: split/join/dedupe order type values into a normalized array
    const normalizeOrderTypes = (raw) => {
      let parts = [];
      if (Array.isArray(raw)) {
        parts = raw.flatMap(p => (p == null ? [] : [String(p)]));
      } else if (raw != null) {
        // some backends return a joined string like "A, B, C"
        parts = String(raw).split(/[,;]\s*/);
      }

      // Trim, remove empties, canonicalize whitespace, and dedupe preserving order
      const cleaned = [];
      const seen = new Set();
      parts.forEach(p => {
        const s = String(p).trim();
        if (!s) return;
        // Normalize multiple spaces and unify spacing after commas
        const norm = s.replace(/\s+/g, ' ');
        if (!seen.has(norm)) {
          seen.add(norm);
          cleaned.push(norm);
        }
      });
      return cleaned;
    };

    const normalized = emailData.map((r) => {
      // Normalize order types into an array of individual tags
      const orderTypeArr = normalizeOrderTypes(r.order_type || r.prediction_order_type_value || []);

      // Backwards-compatible single string representation (joined by comma)
      const joinedOrderType = orderTypeArr.length > 0 ? orderTypeArr.join(', ') : '';

      // Priority mapping (nullable)
      const priority = r.priority ?? r.prediction_priority_value ?? null;

      return {
        ...r,
        order_type: orderTypeArr,
        prediction_order_type_value: joinedOrderType,
        prediction_priority_value: priority,
        prediction_category_value: r.prediction_category_value ?? r.category ?? null,
        classification_timestamp: safeDate(r.classification_timestamp || r.timestamp || r.created_at)
      };
    });

    return normalized;
  } catch (error) {
    console.error('Connection error:', error);
    throw error; // Re-throw the error to be handled by the controller
  }
}

